function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ConfirmationModal, FullColumn, normalizeDescription } from 'seagull';
import ServiceDescription from "../ServiceBuilder/ServiceDescription";
import ServiceNameTextField from "../ServiceBuilder/ServiceNameTextField";
import { useStoreActions } from "../../contexts";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import HelpMeWriteDrawer from "../ServiceBuilder/HelpMeWriteDrawer/HelpMeWriteDrawer";
import { EditServiceContentModalTestIds } from "./EditServiceContentModal.utils";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const EditServiceContentModal = _ref => {
  let {
    serviceName,
    description,
    serviceId,
    title,
    width,
    dismissModal,
    onSave,
    serviceNameBlacklist,
    bundle,
    showAiDescriptionPanel: showAiDescriptionPanelProp = false
  } = _ref;
  const {
    SERVICE_DESCRIPTION_AI
  } = useFeatureFlags();
  const {
    logBI
  } = useStoreActions();
  const form = useForm({
    defaultValues: useMemo(() => ({
      serviceName,
      description,
      bundle: bundle === null || bundle === void 0 ? void 0 : bundle.services
    }), [serviceName, description, bundle === null || bundle === void 0 ? void 0 : bundle.services])
  });
  const [isHelpMeWriteDrawerOpen, setIsHelpMeWriteDrawerOpen] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(564);
  const calculateDrawerWidth = useCallback(() => {
    if (!width) {
      setDrawerWidth(564);
      return;
    }
    const modalElement = document.querySelector("[data-testid=\"".concat(EditServiceContentModalTestIds.EditServiceModal(), "\"]"));
    if (!modalElement) {
      setDrawerWidth(564);
      return;
    }
    const modalRect = modalElement.getBoundingClientRect();
    const translationAmount = 250;
    const modalRightEdgeAfterTranslation = modalRect.right - translationAmount;
    const gap = 32;
    const rightMargin = 32;
    const calculatedWidth = window.innerWidth - modalRightEdgeAfterTranslation - gap - rightMargin;
    setDrawerWidth(Math.min(600, Math.max(300, calculatedWidth)));
  }, [width]);
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      calculateDrawerWidth();
    }, 0);
    const handleResize = () => {
      calculateDrawerWidth();
    };
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [width, calculateDrawerWidth]);
  const onSubmit = data => {
    onSave({
      name: data.serviceName,
      description: data.description
    });
  };
  const isBundle = Boolean(bundle === null || bundle === void 0 ? void 0 : bundle.services);
  const showAiDescriptionPanel = SERVICE_DESCRIPTION_AI && !isBundle && showAiDescriptionPanelProp;
  const disableAiDescriptionPanel = showAiDescriptionPanel && form.watch('serviceName') === '';
  const onHelpMeWriteClick = showAiDescriptionPanel ? () => {
    logBI(17001);
    if (!isHelpMeWriteDrawerOpen) {
      calculateDrawerWidth();
    }
    setIsHelpMeWriteDrawerOpen(prev => !prev);
  } : undefined;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(ConfirmationModal, {
      width: width,
      "data-testid": EditServiceContentModalTestIds.EditServiceModal(),
      title: title,
      style: {
        transform: isHelpMeWriteDrawerOpen ? 'translateX(-250px)' : 'translateX(0)',
        transition: 'transform 0.2s ease-in-out'
      },
      content: /*#__PURE__*/_jsx(FormProvider, _objectSpread(_objectSpread({}, form), {}, {
        children: /*#__PURE__*/_jsx("form", {
          onSubmit: form.handleSubmit(onSubmit),
          children: /*#__PURE__*/_jsxs(FullColumn, {
            gap: 24,
            children: [/*#__PURE__*/_jsx(ServiceNameTextField, {
              testid: EditServiceContentModalTestIds.EditServiceNameInput(),
              serviceNameBlacklist: serviceNameBlacklist,
              disabled: isHelpMeWriteDrawerOpen
            }), /*#__PURE__*/_jsx(ServiceDescription, {
              onHelpMeWriteClick: onHelpMeWriteClick,
              disabledHelpMeWrite: disableAiDescriptionPanel,
              isDrawerOpen: isHelpMeWriteDrawerOpen,
              testId: EditServiceContentModalTestIds.EditServiceDescriptionInput(),
              disabled: isHelpMeWriteDrawerOpen
            })]
          })
        })
      })),
      onPrimaryButtonClick: form.handleSubmit(onSubmit),
      onSecondaryButtonClick: () => dismissModal(),
      primaryButtonLabel: "Save",
      secondaryButtonLabel: "Cancel",
      primaryButtonTestid: EditServiceContentModalTestIds.EditServiceModalSaveButton()
    }), SERVICE_DESCRIPTION_AI && /*#__PURE__*/_jsx(HelpMeWriteDrawer, {
      open: isHelpMeWriteDrawerOpen,
      onClose: () => setIsHelpMeWriteDrawerOpen(false),
      serviceName: serviceName,
      serviceDescription: description,
      serviceId: serviceId,
      drawerWidth: drawerWidth,
      onInsert: parsedRichText => {
        form.setValue('description', normalizeDescription(parsedRichText), {
          shouldDirty: true
        });
      }
    })]
  });
};
export default EditServiceContentModal;